import React, { useState } from "react";
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Grid,
  useToast,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  FiMail,
  FiClock,
  FiSend,
  FiCheck,
  FiDroplet,
  FiGift,
  FiStar,
} from "react-icons/fi";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsSuccess(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      toast({
        title: "Message sent!",
        description: "We'll get back to you as soon as possible.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Box py={12} bg={useColorModeValue("purple.50", "gray.900")}>
      <Container maxW="1400px">
        <VStack spacing={12} align="stretch">
          <VStack spacing={4} textAlign="center">
            <Heading
              size="2xl"
              bgGradient="linear(to-r, purple.500, purple.700)"
              bgClip="text"
            >
              Contact Us
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Have questions about our fragrances? Our fragrance experts are here to help you find your perfect scent.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
            {/* Contact Information */}
            <VStack spacing={8} align="stretch">
              <Box
                p={8}
                bg={bgColor}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow="lg"
              >
                <VStack spacing={6} align="stretch">
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      borderRadius="full"
                      bg="purple.50"
                      _hover={{ bg: "purple.100" }}
                      transition="all 0.2s"
                    >
                      <Icon as={FiMail} w={6} h={6} color="purple.500" />
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="lg">Email</Text>
                      <Text color="gray.600">contact@luxeperfumes.com</Text>
                    </Box>
                  </HStack>
                  <HStack spacing={4}>
                    <Box
                      p={3}
                      borderRadius="full"
                      bg="purple.50"
                      _hover={{ bg: "purple.100" }}
                      transition="all 0.2s"
                    >
                      <Icon as={FiClock} w={6} h={6} color="purple.500" />
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="lg">Business Hours</Text>
                      <Text color="gray.600">
                        Monday - Friday: 9:00 AM - 6:00 PM
                        <br />
                        Saturday: 10:00 AM - 4:00 PM
                        <br />
                        Sunday: Closed
                      </Text>
                    </Box>
                  </HStack>
                </VStack>
              </Box>

              <Box
                p={8}
                bg={bgColor}
                borderRadius="xl"
                borderWidth="1px"
                borderColor={borderColor}
                boxShadow="lg"
              >
                <VStack spacing={6} align="stretch">
                  <Heading size="md" color="purple.600">Fragrance Consultation</Heading>
                  <Text color="gray.600">
                    Our fragrance experts can help you with:
                  </Text>
                  <VStack align="start" spacing={4}>
                    <HStack spacing={3}>
                      <Icon as={FiCheck} color="purple.500" />
                      <Text>Finding your signature scent</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FiCheck} color="purple.500" />
                      <Text>Understanding fragrance families</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FiCheck} color="purple.500" />
                      <Text>Layering recommendations</Text>
                    </HStack>
                    <HStack spacing={3}>
                      <Icon as={FiCheck} color="purple.500" />
                      <Text>Seasonal fragrance advice</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            </VStack>

            {/* Contact Form */}
            <Box
              p={8}
              bg={bgColor}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="lg"
            >
              <form onSubmit={handleSubmit}>
                <VStack spacing={6}>
                  <FormControl isRequired>
                    <FormLabel fontSize="lg">Name</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      size="lg"
                      borderColor="purple.200"
                      _hover={{ borderColor: "purple.400" }}
                      _focus={{ borderColor: "purple.500" }}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="lg">Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      size="lg"
                      borderColor="purple.200"
                      _hover={{ borderColor: "purple.400" }}
                      _focus={{ borderColor: "purple.500" }}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="lg">Subject</FormLabel>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="How can we help?"
                      size="lg"
                      borderColor="purple.200"
                      _hover={{ borderColor: "purple.400" }}
                      _focus={{ borderColor: "purple.500" }}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="lg">Message</FormLabel>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your fragrance preferences..."
                      rows={6}
                      size="lg"
                      borderColor="purple.200"
                      _hover={{ borderColor: "purple.400" }}
                      _focus={{ borderColor: "purple.500" }}
                    />
                  </FormControl>
                  <Button
                    type="submit"
                    colorScheme="purple"
                    size="lg"
                    width="full"
                    height="60px"
                    fontSize="lg"
                    isLoading={isSubmitting}
                    loadingText="Sending..."
                    disabled={isSuccess}
                    leftIcon={isSuccess ? <FiCheck /> : <FiSend />}
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "lg",
                      transition: "all 0.2s",
                    }}
                  >
                    {isSuccess ? "Message Sent!" : "Send Message"}
                  </Button>
                </VStack>
              </form>
            </Box>
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};

export default Contact;
